import React, { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Trash2, ChevronRight, ChevronDown, Loader2 } from "lucide-react";
import { Pill, Card } from "@/components/layout/PageShell";
import MasterCrudShell from "@/components/layout/MasterCrudShell";
import { DataToolbar, DataTable } from "@/components/layout/DataTable";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  main_menu_id: "",
  name: "",
  code: "",
  is_active: true,
};

const PER_PAGE = 25;
const ALL = "all";

function apiError(err, fallback) {
  const errors = err.response?.data?.errors;
  if (errors) return Object.values(errors).flat()[0];
  return err.response?.data?.message || fallback;
}

function useDebounced(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

export default function SubMenuMaster() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [view, setView] = useState("form");
  const [listMode, setListMode] = useState("list");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [categoryId, setCategoryId] = useState(ALL);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const branchId = user?.default_branch?.id;
  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, categoryId]);

  const { data: mainMenus = [] } = useQuery({
    queryKey: ["main-menus", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/main-menus", { params: { branch_id: branchId } });
      return res.main_menus ?? [];
    },
    enabled: Boolean(branchId),
  });

  const { data, isLoading: loadingPaged, isFetching } = useQuery({
    queryKey: ["sub-menus", "paged", branchId, page, debouncedSearch, categoryId],
    queryFn: async () => {
      const { data: res } = await api.get("/sub-menus", {
        params: {
          branch_id: branchId,
          page,
          per_page: PER_PAGE,
          search: debouncedSearch || undefined,
          main_menu_id: categoryId !== ALL ? categoryId : undefined,
        },
      });
      return res;
    },
    enabled: Boolean(branchId) && view === "list" && listMode === "list",
    placeholderData: keepPreviousData,
  });

  const { data: allSubMenus = [], isLoading: loadingTree } = useQuery({
    queryKey: ["sub-menus", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/sub-menus", { params: { branch_id: branchId } });
      return res.sub_menus ?? [];
    },
    enabled: Boolean(branchId) && view === "list" && listMode === "tree",
  });

  const rows = data?.sub_menus ?? [];
  const meta = data?.meta ?? {};
  const total = meta.total ?? rows.length;
  const isLoading = listMode === "list" ? loadingPaged : loadingTree;

  const grouped = useMemo(
    () =>
      mainMenus.map((mm) => ({
        ...mm,
        children: allSubMenus.filter((s) => s.main === mm.name || s.main_menu_id === mm.id),
      })),
    [mainMenus, allSubMenus],
  );

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        main_menu_id: Number(form.main_menu_id),
        name: form.name.trim(),
        code: form.code?.trim() || null,
        is_active: form.is_active,
      };
      if (editingId) {
        return api.put(`/sub-menus/${editingId}`, payload, { params: { branch_id: branchId } });
      }
      return api.post("/sub-menus", payload, { params: { branch_id: branchId } });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["sub-menus"] });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
      toast.success(res.data.message);
      setEditingId(null);
      setForm(EMPTY_FORM);
      setView("form");
    },
    onError: (err) => toast.error(apiError(err, "Failed to save sub menu")),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => api.delete(`/sub-menus/${id}`, { params: { branch_id: branchId } }),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["sub-menus"] });
      queryClient.invalidateQueries({ queryKey: ["menu-items"] });
      toast.success(res.data.message || "Sub menu deleted.");
    },
    onError: (err) => toast.error(apiError(err, "Failed to delete sub menu")),
  });

  function openNew() {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      main_menu_id: mainMenus[0] ? String(mainMenus[0].id) : "",
    });
  }

  function openEdit(row) {
    setEditingId(row.id);
    setForm({
      main_menu_id: String(row.main_menu_id || ""),
      name: row.name || "",
      code: row.code || "",
      is_active: row.is_active ?? row.status === "Active",
    });
    setView("form");
  }

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    if (!form.name.trim()) {
      toast.error("Category name is required.");
      return;
    }
    if (!form.main_menu_id) {
      toast.error("Category is required.");
      return;
    }
    saveMutation.mutate();
  }

  return (
    <MasterCrudShell
      title="Subcategory Master"
      subtitle={`${total} subcategories — ${user?.default_branch?.name || "branch"}`}
      view={view}
      onViewChange={setView}
      isNew={!editingId}
      onSave={handleSave}
      saving={saveMutation.isPending}
      onAddNew={openNew}
      testIdPrefix="sub-menu"
      formTitle={editingId ? form.name || "Edit subcategory" : "New subcategory"}
      formSubtitle={editingId ? "Edit" : "Create"}
      list={
        <Tabs value={listMode} onValueChange={setListMode}>
          <div className="flex items-center justify-between mb-3">
            <TabsList className="bg-slate-100 h-9">
              <TabsTrigger value="list" className="text-[12.5px] h-7 px-3">List View</TabsTrigger>
              <TabsTrigger value="tree" className="text-[12.5px] h-7 px-3">Hierarchy View</TabsTrigger>
            </TabsList>
          </div>

          {isLoading ? (
            <div className="h-32 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <>
              <TabsContent value="list">
                <DataToolbar
                  searchValue={search}
                  onSearch={setSearch}
                  testIdPrefix="sub-menu"
                  extra={
                    <>
                      <Select value={categoryId} onValueChange={setCategoryId}>
                        <SelectTrigger className="h-9 w-[180px]" data-testid="sub-menu-category">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={ALL}>All categories</SelectItem>
                          {mainMenus.map((mm) => (
                            <SelectItem key={mm.id} value={String(mm.id)}>{mm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {isFetching && !loadingPaged ? (
                        <Loader2 size={16} className="animate-spin text-slate-400" />
                      ) : null}
                    </>
                  }
                />
                <DataTable
                  testIdPrefix="sub-menu"
                  emptyText="No subcategories yet"
                  onRowClick={openEdit}
                  pagination={{
                    page: meta.current_page || page,
                    lastPage: meta.last_page || 1,
                    total,
                    from: meta.from || 0,
                    to: meta.to || 0,
                    onPageChange: setPage,
                  }}
                  columns={[
                    { key: "name", label: "Subcategory", bold: true },
                    { key: "main", label: "Category" },
                    { key: "items", label: "Items", align: "right" },
                    { key: "status", label: "Status", render: (v) => <Pill tone={v === "Active" ? "green" : "default"}>{v}</Pill> },
                  ]}
                  rows={rows}
                  rowActions={(row) => [
                    { title: "Edit", icon: <Edit3 size={14} />, onClick: () => openEdit(row) },
                    {
                      title: "Delete",
                      icon: <Trash2 size={14} />,
                      danger: true,
                      onClick: () => {
                        if (window.confirm(`Delete category "${row.name}"?`)) deleteMutation.mutate(row.id);
                      },
                    },
                  ]}
                />
              </TabsContent>

              <TabsContent value="tree">
                <div className="space-y-2">
                  {grouped.map((mm) => (
                    <Card key={mm.id} className="p-3">
                      <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
                        <ChevronDown size={14} className="text-slate-400" />
                        {mm.name}
                        <Pill tone="blue" className="ml-1">{mm.children.length} sub</Pill>
                      </div>
                      <ul className="mt-2 ml-6 space-y-1">
                        {mm.children.map((s) => (
                          <li key={s.id} className="flex items-center gap-2 text-[12.5px] text-slate-600 py-1">
                            <ChevronRight size={12} className="text-slate-300" />
                            {s.name}
                            <button type="button" className="ml-auto text-[11px] text-blue-600 hover:underline" onClick={() => openEdit(s)}>
                              Edit
                            </button>
                          </li>
                        ))}
                      </ul>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            </>
          )}
        </Tabs>
      }
    >
      <div className="space-y-4 max-w-lg">
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Category</Label>
          <Select value={form.main_menu_id} onValueChange={(v) => setField("main_menu_id", v)}>
            <SelectTrigger className="h-9"><SelectValue placeholder="Select category" /></SelectTrigger>
            <SelectContent>
              {mainMenus.map((mm) => (
                <SelectItem key={mm.id} value={String(mm.id)}>{mm.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Category name</Label>
          <Input
            value={form.name}
            onChange={(e) => setField("name", e.target.value)}
            className="h-9"
            placeholder="e.g. Shawarma"
            autoFocus
          />
        </div>
        <div>
          <Label className="text-[12px] text-slate-600 mb-1 block">Code (optional)</Label>
          <Input value={form.code} onChange={(e) => setField("code", e.target.value)} className="h-9" />
        </div>
        <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-white max-w-xs">
          <Switch checked={form.is_active} onCheckedChange={(v) => setField("is_active", v)} />
          <span className="text-[12px] text-slate-700">Active on POS</span>
        </div>
      </div>
    </MasterCrudShell>
  );
}
